import { BaseChart } from './base-chart-model';
import { IChart, IAxis, IChartSeries, IChartData, ITargetLine } from './interfaces';
import { getFormattedNumberAbbrev } from '../utils';
import { getAxisScaleBand, getAxisScaleLinear } from '../utils/axis-helper';
import { axisBottom, axisLeft } from 'd3-axis';

/********* Interfaces **********************/
export interface ILineChart extends IChart {
  width?: number;
  height?: number;
  xAxis?: IAxis;
  yAxis?: IAxis;
  strokeWidth?: number;
  stretchToMaxWidth?: boolean;
  target?: ITargetLine;
  isPercentage?: boolean;
  tooltipMarkerDotRadius?: number;
}

/********* Defaults **********************/
const DEFAULT_WIDTH = 800; // default svg width
const DEFAULT_HEIGHT = 400; // default svg height
const DEFAULT_TOOLTIP_MARKER_RADIUS = 2.5; // default tooltip marker line dot radius
const DEFAULT_NUMBER_OF_TICKS = 5; // default y axis number of ticks

const MARGIN_TOP = 20; // chart top margin
const MARGIN_RIGHT = 20; // chart right margin
const MARGIN_BOTTOM = 50; // chart bottom margin
const MARGIN_LEFT = 60; // chart left margin
const SCALE_BAND_OUTER_PADDING = 0.2; // x axis scale band outer padding

export class LineChart extends BaseChart implements ILineChart {
  public height!: number;
  public width!: number;
  public xAxis: IAxis;
  public yAxis: IAxis;
  public strokeWidth!: number;
  public stretchToMaxWidth: boolean;
  public plotAreaLeftMargin!: number;
  public plotAreaTopMargin!: number;
  public target: ITargetLine;
  public isPercentage: boolean;
  public tooltipMarkerDotRadius!: number;
  public plotAreaWidth!: number;

  constructor(obj: ILineChart) {
    super(obj);
    Object.assign(this, obj);
    this.strokeWidth = obj.strokeWidth || 2;
    this.width = obj.width || DEFAULT_WIDTH;
    this.height = DEFAULT_HEIGHT;
    this.plotAreaLeftMargin = MARGIN_LEFT;
    this.plotAreaTopMargin = MARGIN_TOP;
    this.xAxis.showAxisLabel = obj.xAxis.showAxisLabel || true;
    this.yAxis.showAxisLabel = obj.yAxis.showAxisLabel || true;
    this.isPercentage = obj.isPercentage || false;
    this.stretchToMaxWidth = obj.stretchToMaxWidth || false;
    this.tooltipMarkerDotRadius = obj.tooltipMarkerDotRadius || DEFAULT_TOOLTIP_MARKER_RADIUS;
    this.xAxis.numberOfTicks = obj.series[0].data.length;
    this.yAxis.numberOfTicks = obj.yAxis.numberOfTicks || DEFAULT_NUMBER_OF_TICKS;
  }

  public get data(): IChartSeries[] {
    return this.series;
  }

  public get xDomain(): string[] {
    return this.series[0]?.data?.map(d => d.name);
  }

  public get yDomain(): number[] {
    let maxVal = 0;

    this.series.forEach(s => {
      s?.data.forEach(d => {
        maxVal = maxVal > d.y ? maxVal : d.y;
      });
    });

    maxVal = this.isPercentage 
        ? Math.max(100, maxVal)
        : maxVal;
    return [0, maxVal + 10];
  }

  public get yScale(): any {
    return getAxisScaleLinear(
        this.yDomain,
        this.plotAreaHeight,
        0,
        this.yAxis.tickValues
      );
  }

  public get xScale(): any {
    return getAxisScaleBand(
        this.xDomain,
        0,
        this.plotAreaWidth,
        0,
        SCALE_BAND_OUTER_PADDING
      );
  }

  public get xScaleBandwidth(): any {
    return this.xScale.bandwidth;
  }

  public get leftAxis(): any {
    return axisLeft(this.yScale)
      .tickFormat(x => this.getFormattedAxisTick(x.toString()))
      .ticks(this.yAxis.numberOfTicks)
      .tickSize(this.yAxisTickSize);
  }

  public get bottomAxis(): any {
    return axisBottom(this.xScale)
      .ticks(this.xAxis.numberOfTicks)
      .tickSize(this.xAxisTickSize)
      .tickPadding(10);
  }

  public get plotAreaHeight(): number {
    return this.height - MARGIN_TOP - MARGIN_BOTTOM;
  }

  public getLineOriginCoordinateX(
    xScale: any,
    data: IChartData
  ): number {
    return xScale(data.name) + this.xScaleBandwidth()/2;
  }

  public getLineOriginCoordinateY(
    yScale: any,
    data: IChartData,
  ): number {
    return yScale(data.y);
  }

  public get valuePrefix(): string {
    return this.series[0]?.valuePrefix;
  }

  public get valueSuffix(): string {
    return this.series[0]?.valueSuffix;
  }

  public getFormattedAxisTick(
    tickValue: string,
    decimalPlace: number = 0
  ) {   
    return `${this.valuePrefix || ''}${getFormattedNumberAbbrev(
      +tickValue,
      this.formatCountryCode,
      decimalPlace
    ).toString()}${this.valueSuffix || ''}`;
  }

  public get xAxisTickSize(): number {
    return this.xAxis.showGridLines 
      ? -this.plotAreaHeight
      : 0;
  }

  public get yAxisTickSize(): number {
    return this.yAxis.showGridLines
      ? -this.plotAreaWidth
      : 0;
  }

  public setPlotAreaWidth(width: number) {
    this.plotAreaWidth = width - this.plotAreaLeftMargin - MARGIN_RIGHT;
  }
}
